import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Container, Flex, Text } from '@chakra-ui/react';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { withCommonServerSideProps } from '@/lib/withCommonServerSideProps';

import { H1 } from '@/components/Heading';
import NotFound from '@/components/NotFound';
import Seo from '@/components/Seo';
import { SortingProducts, SortOptionValue } from '@/components/Sorting/SortingProducts';
import { Button } from '@/components/UI/Buttons/Button';

import { ProductList } from '@/layouts/ProductsSection/ProductList';
import { Product } from '@/models/api';
import { CategoryProductListProps, Sort, SortDir } from '@/models/props/ProductCardProps';
import { ProductService } from '@/services';
import { EnvironmentService } from '@/services/EnvironmentService';

export type NewProductsPageProps = {
    products: Product[];
    pageNo: number;
    totalPageCount: number;
    totalCount: number;
    session: Session | null | undefined;
};

export const NewProductsPage: NextPage<NewProductsPageProps> = (props) => {
    const intl = useIntl();
    const { products, pageNo, totalCount, totalPageCount, session } = props;
    const [loading, setLoading] = useState(false);
    const [currentPageNo, setCurrentPageNo] = useState(pageNo);
    const [currentProductList, setCurrentProductList] = useState<Product[]>(products);
    const [sort, setSort] = useState<Sort | undefined>();
    const [sortDir, setSortDir] = useState<SortDir | undefined>();

    const fetchProductsHandler = useCallback(
        async (pageNo: number, sort?: Sort, sortDir?: SortDir) => {
            const loadMoreProducts: CategoryProductListProps = await ProductService.getNewProducts({
                pageNo: pageNo,
                sort,
                sortDir,
                opts: {
                    session: session
                }
            });

            setLoading(false);

            return loadMoreProducts;
        },
        [session]
    );

    const loadMoreHandler = useCallback(
        async (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.preventDefault();
            setLoading(() => true);
            setCurrentPageNo(currentPageNo + 1);
            const productList = await fetchProductsHandler(currentPageNo + 1, sort, sortDir);
            const newItems = [...currentProductList, ...productList.items];
            setCurrentProductList(newItems);
        },
        [currentPageNo, sort, sortDir, currentProductList, fetchProductsHandler]
    );

    const sortCallback = useCallback(
        async (value: SortOptionValue) => {
            setLoading(true);
            const sort = value.sort as Sort;
            const sortDir = value.sortDir as SortDir;
            setLoading(true);
            setCurrentPageNo(1);
            const productList = await fetchProductsHandler(1, sort, sortDir);
            setCurrentProductList(productList.items);
            setSortDir(sortDir);
            setSort(sort);
        },
        [fetchProductsHandler]
    );

    return (
        <>
            <Seo title={intl.formatMessage({ id: 'page.new-products.title' })} />
            <Container maxW="full" px={8} pb={14} pt={8}>
                <Box>
                    <H1 textAlign="center" mb={3}>
                        <FormattedMessage id="page.new-products.title" />
                    </H1>
                </Box>
                {currentProductList.length > 0 && (
                    <Flex
                        my={6}
                        flexDirection={{ base: 'column', md: 'row' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <SortingProducts selectValue={sortCallback}></SortingProducts>
                        <Text textColor="grey.600" mt={{ base: 4, md: 0 }}>
                            <FormattedMessage id="total-count"></FormattedMessage>: {totalCount}
                        </Text>
                    </Flex>
                )}
                <Box pt={6}>
                    {currentProductList.length > 0 ? (
                        <ProductList items={currentProductList} />
                    ) : (
                        <NotFound message="page.new-products.not-results" />
                    )}
                </Box>
                {currentPageNo < totalPageCount && (
                    <>
                        <Flex justifyContent="center" pt={16}>
                            <Button
                                onClick={loadMoreHandler}
                                fontSize="xl"
                                h={20}
                                py={3}
                                px={12}
                                rounded="full"
                                rightIcon={<ChevronDownIcon />}
                                isLoading={loading}
                                spinnerPlacement="end"
                                loadingText={intl.formatMessage({ id: 'loading' })}
                            >
                                <FormattedMessage id="see-more-products"></FormattedMessage>
                            </Button>
                        </Flex>
                    </>
                )}
            </Container>
        </>
    );
};

export const getServerSideProps = withCommonServerSideProps(
    async (
        context: GetServerSidePropsContext
    ): Promise<GetServerSidePropsResult<NewProductsPageProps>> => {
        if (+EnvironmentService.getAppStaging() < 2) {
            return {
                notFound: true
            };
        }
        const session = await getSession(context);
        let products: Product[];
        let currentPageNo = 1;
        let totalPageCount = 1;
        let totalCount = 0;

        try {
            const data = await ProductService.getNewProducts({
                opts: {
                    session
                }
            });
            products = data.items;
            currentPageNo = data.currentPage;
            totalPageCount = data.totalPageCount;
            totalCount = data.totalCount;
        } catch {
            products = [];
        }

        return {
            props: {
                products,
                pageNo: currentPageNo,
                totalPageCount,
                totalCount,
                session
            }
        };
    }
);

export default NewProductsPage;
